/**
 * v3.17-1241 (OPS-002) — Reusable Client Selector.
 *
 * NOT Ghost-specific. Any operational activity in the Hub that needs
 * to link a client (Ghost, Priority Queue, Phone Calls, Tasks,
 * Appointments, Follow-ups, custom workflows) can drop this in.
 *
 * Contract:
 *   • Debounced search against `/api/clients?search=…`.
 *   • Displays Name / Client ID / Company for every result.
 *   • Emits an object of shape { id, full_name, company_name } on select.
 *   • Optional pre-selected client via `defaultClient`.
 *   • Supports clearing selection.
 *
 * The component intentionally does not know what module is consuming
 * it. Persistence is the consumer's responsibility.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import api from '../lib/api';
import { Search, X, User2 } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { CF360TransferBadge } from './CF360TransferNotice';


export const ClientSelector = ({
  value,               // { id, full_name, company_name } | null
  onChange,            // (client | null) => void
  placeholder = 'Link a client (optional)…',
  testIdPrefix = 'client-selector',
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await api.get('/clients', { params: { search: q || undefined, limit: 20 } });
        const list = Array.isArray(res?.data) ? res.data
                     : (res?.data?.clients || res?.data?.items || []);
        setRows(list);
      } catch (_e) {
        setRows([]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [q, open]);

  const label = useMemo(() => {
    if (!value) return '';
    return `${value.full_name || 'Client'}${value.id ? ` · ${String(value.id).slice(0, 8)}` : ''}`;
  }, [value]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          data-testid={`${testIdPrefix}-trigger`}
          className={`w-full flex items-center gap-2 rounded-md border px-3 py-2 text-sm text-left
                      ${value ? 'border-slate-300 bg-white text-slate-900'
                              : 'border-slate-300 bg-white text-slate-500'}
                      hover:border-slate-400 focus:outline-none focus:ring-2 focus:ring-accent
                      disabled:opacity-50`}
        >
          <User2 className="h-3.5 w-3.5 text-slate-400 shrink-0" />
          <span className="flex-1 truncate">
            {value ? label : placeholder}
          </span>
          {value && (
            <span
              role="button"
              tabIndex={-1}
              onClick={(e) => { e.stopPropagation(); onChange?.(null); }}
              data-testid={`${testIdPrefix}-clear`}
              className="h-5 w-5 rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-700 flex items-center justify-center"
              title="Clear"
            >
              <X className="h-3 w-3" />
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[360px] p-2 bg-white border border-slate-200 shadow-xl"
        data-testid={`${testIdPrefix}-popover`}
      >
        <div className="flex items-center gap-2 px-2 py-1.5 bg-slate-50 rounded mb-1">
          <Search className="h-3.5 w-3.5 text-slate-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name, phone, or email…"
            className="bg-transparent outline-none text-xs text-slate-900 placeholder:text-slate-400 flex-1"
            data-testid={`${testIdPrefix}-search`}
            autoFocus
          />
        </div>
        <div className="max-h-64 overflow-y-auto">
          {loading && (
            <div className="text-xs text-slate-500 px-2 py-4 text-center">Searching…</div>
          )}
          {!loading && rows.length === 0 && (
            <div className="text-xs text-slate-500 px-2 py-4 text-center">
              {q ? 'No matches.' : 'Type to search clients.'}
            </div>
          )}
          {rows.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => { onChange?.({ id: c.id, full_name: c.full_name, company_name: c.company_name }); setOpen(false); }}
              data-testid={`${testIdPrefix}-option-${c.id}`}
              className="w-full text-left flex items-start gap-2 px-2 py-1.5 rounded hover:bg-slate-50 transition"
            >
              <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-accent/15 text-accent text-[10px] font-bold shrink-0 mt-0.5">
                {(c.full_name || '?').slice(0, 2).toUpperCase()}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-xs font-semibold text-slate-900 truncate">
                  {c.full_name || 'Unnamed client'}{' '}
                  <CF360TransferBadge transfer={c.cf360_transfer} testId={`${testIdPrefix}-cf360-${c.id}`} />
                </span>
                <span className="block text-[10px] text-slate-500 truncate">
                  {c.id?.slice(0, 8) || ''}{c.company_name ? ` · ${c.company_name}` : ''}
                </span>
              </span>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};


export default ClientSelector;
